import styled from "@emotion/styled";
import { ExpandMore } from "@mui/icons-material";
import { ColorPickerBackground, RowBox } from "components/common";
import {
    CHART_DUPLICATE_COLOR_MESSAGE,
    COLORS,
    DASHBOARD_CHART_LIMIT_MESSAGE,
    DASHBOARD_CHART_MAX,
} from "constant";
import { PanelV2 } from "generated/models";
import { useMetricDefined } from "hooks/useMetricDefined";
import * as _ from "lodash";
import React, { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import Row from "react-bootstrap/Row";
import { ColorResult, SketchPicker } from "react-color";
import { useHistory, useParams } from "react-router-dom";
import dashboardService from "service/dashboardService";
import { Device, WidgetItem, WidgetSource, WidgetSubmitData } from "types";
import { showErrorAlert } from "utils/alert";
import { parseColorToHex } from "utils/functions";
import { fetchDevices } from "utils/gatewayFunctions";
import { create } from "zustand";
import { getWidgetConfigByWidgetType, WidgetType } from "../utils/widgetConfig";
import { isCreateSubmitInvalid } from "../utils/submitValidation";
import { useAppContext } from "context/appContext";
import CreateWidget from "./CreateWidget";
import { widgetTemplateCategories } from "./data/widget";

export const ContainerStyled = styled(Container)`
    max-width: 100%; 

    #chart-edit-form .table-row {
        background-color: var(--create-chart-from-background);
    }

    #chart-edit-form .form-control + .form-control,
    #chart-edit-form .custom-control + .custom-control {
        margin-top: 0.75rem;
    }

    #chart-edit-form .custom-control {
        height: 38px;
        display: flex;
        align-items: center;
    }

    #chart-edit-form .form-control,
    #device-form .form-control {
        background-color: var(--create-chart-form-secondary);
    }

    #chart-edit-form .custom-control-label {
        padding-left: 10px;
    }
`;

const ImagePreviewContainer = styled.div`
    width: 100%;
    height: auto;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
        border-radius: 4px;
        object-fit: cover;
        background: transparent;
        width: 100%;
        height: 100%;
    }
`;

const ContentWrapper = styled.div`
    #chart-edit-form .table-row {
        background-color: var(--create-chart-from-background);
    }

    #chart-edit-form .form-control + .form-control,
    #chart-edit-form .custom-control + .custom-control {
        margin-top: 0.75rem;
    }

    #chart-edit-form .custom-control {
        height: 38px;
        display: flex;
        align-items: center;
    }

    #chart-edit-form .form-control,
    #device-form .form-control {
        background-color: var(--create-chart-form-secondary);
    }

    #chart-edit-form .custom-control-label {
        padding-left: 10px;
    }
`;

// eslint-disable-next-line no-control-regex
export const CHART_NAME_PATTERN = /^.{1,64}$/;
export const CHART_ERROR_MESSAGE =
    "Invalid widget name. Widget names should consist of 1 to 64 characters only.";

// Create color picker component with Dropdown bootstrap component
// https://react-bootstrap.github.io/components/dropdowns/#dropdown-button-props
type ColorPickerProps = {
    defaultValue?: string;
    disabled?: boolean;
    onChange?: (event: Event | React.BaseSyntheticEvent, value: string) => void;
    className?: string;
};

type UnitPickerProps = {
    data?: string[];
    defaultValue?: string;
    onChange?: (event: Event | React.BaseSyntheticEvent, value: string) => void;
    className?: string;
};

type ColorEvent = { source: string } & ColorResult;

const decimalToHex = (alpha: number) =>
    Math.round(255 * alpha)
        .toString(16)
        .padStart(2, "0");

export const ColorPicker: React.FunctionComponent<ColorPickerProps> = (
    props
) => {
    const { defaultValue, onChange, disabled } = props;
    const [color, setColor] = React.useState<string>(
        parseColorToHex(defaultValue ?? COLORS[0])
    );

    const handleColorChange = (color: ColorEvent) => {
        let hexCode = color.hex;
        let rgbaCode = color.hex;
        if (color.source === "rgb") {
            hexCode = `${color.hex}${decimalToHex(color.rgb.a as number)}`;
            rgbaCode = `#${decimalToHex(
                color.rgb.a as number
            )}${color.hex.substring(1)}`;
        }

        setColor(hexCode);

        onChange?.(new Event("change"), rgbaCode);
    };

    React.useEffect(() => {
        defaultValue && setColor(parseColorToHex(defaultValue));
    }, [defaultValue]);

    return (
        <Dropdown className="color-picker">
            <Dropdown.Toggle
                variant="success"
                id="dropdown-color-picker"
                className={
                    props.className
                        ? "color-picker__toggle " + props.className
                        : "color-picker__toggle unit-picker-toggle"
                }
                disabled={disabled}
                role="div"
                bsPrefix="dropdown-toggle-no-caret"
            >
                <ColorPickerBackground $inputColor={color} className="w-32px">
                    &nbsp;
                </ColorPickerBackground>
                <ExpandMore fontSize="medium" aria-label="expand_more" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="color-picker-dropdown-menu">
                <SketchPicker
                    color={color}
                    onChangeComplete={(color) => {
                        handleColorChange(color as ColorEvent);
                    }}
                    presetColors={COLORS}
                />
            </Dropdown.Menu>
        </Dropdown>
    );
};

export const UnitPicker: React.FunctionComponent<UnitPickerProps> = (
    props
) => {
    const { data, defaultValue, onChange } = props;
    const [value, setValue] = React.useState<string>(defaultValue ?? "");

    React.useEffect(() => {
        onChange?.(
            new Event("change", {
                bubbles: true,
            }),
            value
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <Dropdown className="unit-picker">
            <Dropdown.Toggle
                variant="success"
                className={
                    props.className
                        ? "unit-picker__toggle " + props.className
                        : "unit-picker__toggle unit-picker-toggle"
                }
                disabled
            >
                <span className="h-17px d-flex align-items-center">
                    {value}
                </span>
                <ExpandMore fontSize="medium" aria-label="expand_more" />
            </Dropdown.Toggle>

            <Dropdown.Menu>
                {data?.map((item, index) => (
                    <Dropdown.Item
                        key={index + item}
                        onClick={() => {
                            setValue(item);
                        }}
                    >
                        <div className="unit-picker__input">{item}</div>
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export type CreateChartState = {
    title?: string;
    /** @deprecated Use the "configuration" instead */
    multiple?: boolean;
    data: any[];
    columns: {
        key: string;
        name: string;
        className?: string;
        render?: (data: any) => JSX.Element | null;
    }[];
    transform: (data: any, widgetType: WidgetType) => { [key: string]: any };
    validate?: () =>
        | {
            isValid: boolean | "true" | "false";
            message: string;
        }[]
        | undefined;
};

export const useCreateDashboardStore = create<{
    loading: boolean;
    setLoading: (loading: boolean) => void;
    dashboardId: string;
    setDashboardId: (dashboard_id: string) => void;
    widgetSource: WidgetSource;
    setWidgetSource: (widget_source: WidgetSource) => void;
    createData: CreateChartState | undefined;
    createModalShow: boolean;
    setCreateModalShow: (show: boolean) => void;
    allRegistry: any[];
    deviceList: Device[];
    currentCharts: PanelV2[];
    setCurrentCharts: (charts: PanelV2[]) => void;
    widget: WidgetItem | null;
    setWidget: (widget: WidgetItem) => void;
}>((set, get) => ({
    loading: false,
    setLoading: (loading) => set({ loading }),
    dashboardId: "",
    currentCharts: [],
    setCurrentCharts: (charts: PanelV2[]) => set({ currentCharts: charts }),
    setDashboardId: (dashboard_id: string) =>
        set({ dashboardId: dashboard_id }),
    widgetSource: WidgetSource.Sensor,
    setWidgetSource: (widget_source: WidgetSource) =>
        set({ widgetSource: widget_source }),
    createData: undefined,
    createModalShow: false,
    setCreateModalShow: (show: boolean) => {
        if (!show) {
            set({ createModalShow: false });
            return;
        }

        const createData = get().createData;
        const validate = createData?.validate?.() || [];
        const isValid = validate.every((item) => item.isValid);
        const currentCharts = get().currentCharts;
        const widgetSource = get().widgetSource;
        const totalChart =
            currentCharts.length + (createData?.data?.length ?? 0);

        if (
            (WidgetSource.Sensor === widgetSource ||
                WidgetSource.Actuator === widgetSource ||
                WidgetSource.Meter === widgetSource) &&
            createData?.multiple
        ) {
            const invalid = createData?.data.some(({ attributes }: any) => {
                const uniqueColors = _.unionBy(attributes, ({ color }) =>
                    _.lowerCase(color)
                );
                if (uniqueColors.length < attributes.length) {
                    return true;
                }
                return false;
            });

            if (invalid) {
                showErrorAlert({
                    message: CHART_DUPLICATE_COLOR_MESSAGE,
                });
                return;
            }
        }

        if (totalChart > DASHBOARD_CHART_MAX) {
            showErrorAlert({
                message: DASHBOARD_CHART_LIMIT_MESSAGE.fill({
                    limit: DASHBOARD_CHART_MAX,
                }),
            });

            return;
        }

        if (isValid) {
            set({ createModalShow: true });
        } else {
            validate.forEach(
                ({ message, isValid }) =>
                    !isValid && showErrorAlert({ message })
            );
        }
    },
    allRegistry: [],
    deviceList: [],
    widget: null,
    setWidget: (widget: WidgetItem) => {
        set({ widget: widget });
    },
}));

type CreateWidgetPanelProp = {
    widgetSource: WidgetSource;
    widget: WidgetItem | null;
};

const DashboardCreateBody = ({
    widgetSource,
    widget,
}: CreateWidgetPanelProp) => {
    let LoadComponent = null;

    switch (widgetSource) {
        case WidgetSource.Sensor:
            LoadComponent = require("./SensorCharts").default;
            break;
        case WidgetSource.Actuator:
            LoadComponent = require("./ActuatorCharts").default;
            break;
        case WidgetSource.Meter:
            LoadComponent = require("./ConsumptionCharts").default;
            break;
        case WidgetSource.Gateway:
            LoadComponent = require("./GatewayCharts").default;
            break;
        case WidgetSource.PSL:
            LoadComponent = require("./PSLWidget").default;
            break;
        case WidgetSource.LDSU:
            LoadComponent = require("./LDSUWidget/LDSUCharts").default;
            break;
        case WidgetSource.Event:
            LoadComponent = require("./EventAndAlertWidget").default;
            break;
        case WidgetSource.Subscription:
            LoadComponent = require("./SubscriptionWidgets").default;
            break;
        default:
            LoadComponent = require("./EventAndAlertWidget").default;
            break;
    }

    return <LoadComponent widget={widget} />;
};

const isDisabledSubmit = ({
    createData,
    widgetType,
    widgetSource,
}: {
    createData: CreateChartState | undefined;
    widgetType?: WidgetType;
    widgetSource: WidgetSource;
}) => {
    if (!createData?.data?.length) {
        return true;
    }

    const widgetConfiguration =
        typeof widgetType === "number"
            ? getWidgetConfigByWidgetType(widgetType)
            : undefined;

    return isCreateSubmitInvalid({
        createData,
        widgetType,
        widgetConfig: widgetConfiguration,
        isPSLWidgetSource: widgetSource === WidgetSource.PSL,
    });
};

const ImagePreview = (
    widgetImage:
        | string
        | React.ComponentType<React.SVGProps<SVGSVGElement>>
        | undefined,
    imageImage: string
) => {
    if (typeof widgetImage === "string") {
        return (
            <img
                src={widgetImage}
                alt={imageImage}
                style={{ width: "100%", objectFit: "cover" }}
            />
        );
    }
    if (widgetImage) {
        const PreviewComponent = widgetImage;
        return <PreviewComponent />;
    }
    return <></>;
};

const DashboardCreate = () => {
    const params: { dashboardId: string } = useParams();
    const history = useHistory();
    useMetricDefined();
    const {
        storeData: {
            organization: { currentUserIsOwner },
        },
    } = useAppContext();
    const {
        widgetSource,
        setDashboardId,
        setWidgetSource,
        setCreateModalShow,
        setLoading,
        createData,
        widget,
        setWidget,
    } = useCreateDashboardStore();
    const [nextStep, setNextStep] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState<WidgetItem | null>(
        null
    );

    // Hide subscription widgets for non-owner users
    const filteredTemplateCategories = useMemo(
        () =>
            currentUserIsOwner
                ? widgetTemplateCategories
                : widgetTemplateCategories.filter(
                    (category) => category.id !== WidgetSource.Subscription
                ),
        [currentUserIsOwner]
    );

    const disabled = isDisabledSubmit({
        createData,
        widgetType: widget?.widgetType,
        widgetSource,
    });

    React.useEffect(() => {
        setWidgetSource(WidgetSource.Sensor);
        setNextStep(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const { dashboardId } = params;
        setDashboardId(dashboardId);

        const fetch = async () => {
            setLoading(true);
            const devices = await fetchDevices();

            const { data = [] } = await dashboardService.getPanelDashboard(
                dashboardId,
                {
                    offset: 0,
                    limit: 100,
                }
            );

            useCreateDashboardStore.setState({
                ...devices,
                currentCharts: data,
            });

            setLoading(false);
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.dashboardId]);

    const onSelectWidget = (widgetData: WidgetSubmitData) => {
        const { widgetSource, widgetTemplate } = widgetData;
        setNextStep(true);
        setWidgetSource(widgetSource);
        setWidget(widgetTemplate);
        setSelectedWidget(widgetTemplate);
    };

    return (
        <ContentWrapper className="page-content">
            {nextStep && (
                <ContainerStyled>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">Create widgets</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="mr-md-2">
                            <RowBox>
                                <div className="px-2 py-2">
                                    <Row>
                                        <Col>
                                            <div>{selectedWidget?.name}</div>
                                            <p className="reusable-description">
                                                {selectedWidget?.description}
                                            </p>
                                        </Col>
                                    </Row>
                                    <DashboardCreateBody
                                        widgetSource={widgetSource}
                                        widget={selectedWidget}
                                    />
                                </div>
                            </RowBox>
                        </Col>
                        <Col xs={12} lg={3} xl={3}>
                            <RowBox
                                className="d-flex flex-column px-3 py-3"
                                borderRadius="10px"
                            >
                                <div className="mb-2">Example</div>
                                <ImagePreviewContainer>
                                    {/* Widget preview image (full card preview, not icon) */}
                                    {ImagePreview(
                                        selectedWidget?.ImagePreview,
                                        selectedWidget?.name ?? ""
                                    )}
                                </ImagePreviewContainer>
                            </RowBox>

                            <div className="d-flex justify-content-end align-items-center mt-3 w-100">
                                <Button
                                    className="text-uppercase flex-md-grow-1"
                                    variant="secondary"
                                    onClick={() => {
                                        history.push("/dashboard");
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    className="ml-2 text-uppercase flex-md-grow-1"
                                    variant="primary"
                                    onClick={() => setCreateModalShow(true)}
                                    disabled={disabled}
                                >
                                    Create
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </ContainerStyled>
            )}

            {!nextStep && (
                <CreateWidget
                    onSubmit={onSelectWidget}
                    templateCategories={filteredTemplateCategories}
                />
            )}
        </ContentWrapper>
    );
};

export default DashboardCreate;
